import { CASE_STATUSES, createChecklistSchema, type CaseStatus, type ChecklistDto } from '@tms/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import {
  Button,
  Dialog,
  Field,
  Input,
  SelectMenu,
  SelectMenuItem,
  Textarea,
} from '../../components/ui';
import { useEnumLabels, useErrorText, useT } from '../../i18n';
import { CaseAttributeSelect } from '../case-attributes/CaseAttributeSelect';
import { EnvironmentSelect } from '../environments/EnvironmentSelect';
import { flattenFolders, useFolderTree } from '../folders/api';
import { LabelPicker } from '../labels/LabelPicker';
import { useCreateChecklist, useUpdateChecklist } from './api';

interface ChecklistValues {
  title: string;
  description?: string | null;
  /** Empty only until a folder is chosen; a checklist always has one. */
  folderId: string;
  status: CaseStatus;
  /** `null` when the checklist is not tied to a particular stand. */
  environmentId?: string | null;
  /** `null` until the checklist is classified. */
  layerId?: string | null;
  moduleId?: string | null;
}

export interface ChecklistFormDialogExtras {
  /** Folder to start from, when the list already has one selected. */
  defaultFolderId?: string | undefined;
}

export interface ChecklistFormDialogProps extends ChecklistFormDialogExtras {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectCode: string;
  checklist?: ChecklistDto;
  onCreated?: (checklist: ChecklistDto) => void;
}

export const ChecklistFormDialog = ({
  open,
  onOpenChange,
  projectCode,
  checklist,
  defaultFolderId,
  onCreated,
}: ChecklistFormDialogProps) => {
  const { t } = useT();
  const { errorText, applyApiError } = useErrorText();
  const { caseStatus } = useEnumLabels();
  const isEdit = checklist !== undefined;
  const [labelIds, setLabelIds] = useState<string[]>([]);
  const create = useCreateChecklist(projectCode);
  const update = useUpdateChecklist(projectCode, checklist?.id ?? '');
  const { data: folders } = useFolderTree(projectCode, 'checklist');

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ChecklistValues>({
    resolver: zodResolver(createChecklistSchema.omit({ items: true })),
    defaultValues: {
      title: '',
      description: '',
      folderId: '',
      status: 'active',
      environmentId: null,
      layerId: null,
      moduleId: null,
    },
  });

  useEffect(() => {
    if (!open) return;
    reset({
      title: checklist?.title ?? '',
      description: checklist?.description ?? '',
      folderId: checklist?.folderId ?? defaultFolderId ?? '',
      status: checklist?.status ?? 'active',
      environmentId: checklist?.environment?.id ?? null,
      layerId: checklist?.layer?.id ?? null,
      moduleId: checklist?.module?.id ?? null,
    });
    setLabelIds(checklist?.labels.map((label) => label.id) ?? []);
  }, [open, checklist, defaultFolderId, reset]);

  const onSubmit = handleSubmit(async (values) => {
    const description = values.description?.trim() ? values.description : null;
    try {
      if (isEdit) {
        await update.mutateAsync({
          title: values.title,
          description,
          folderId: values.folderId,
          status: values.status,
          environmentId: values.environmentId ?? null,
          layerId: values.layerId ?? null,
          moduleId: values.moduleId ?? null,
          labelIds,
        });
        toast.success(t('checklists.updated'));
      } else {
        const created = await create.mutateAsync({
          title: values.title,
          description,
          folderId: values.folderId,
          status: values.status,
          environmentId: values.environmentId ?? null,
          layerId: values.layerId ?? null,
          moduleId: values.moduleId ?? null,
          labelIds,
          items: [],
        });
        toast.success(t('checklists.created', { key: created.checklistKey }));
        onCreated?.(created);
      }
      onOpenChange(false);
    } catch (error) {
      if (
        !applyApiError(error, setError, [
          'title',
          'description',
          'folderId',
          'status',
          'environmentId',
          'layerId',
          'moduleId',
        ])
      ) {
        toast.error(errorText(error, 'checklists.saveFailed'));
      }
    }
  });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? t('checklists.editTitle') : t('checklists.new')}
      description={isEdit ? undefined : t('checklists.formHint')}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={() => void onSubmit()} loading={isSubmitting}>
            {isEdit ? t('common.saveChanges') : t('checklists.create')}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void onSubmit();
        }}
        className="flex flex-col gap-4"
        noValidate
      >
        <Field label={t('common.title')} error={errors.title?.message} required>
          {({ id, invalid, describedBy }) => (
            <Input
              id={id}
              invalid={invalid}
              aria-describedby={describedBy}
              autoFocus
              placeholder={t('checklists.titlePlaceholder')}
              {...register('title')}
            />
          )}
        </Field>
        <Field label={t('common.folder')} error={errors.folderId?.message} required>
          {({ id, invalid, describedBy }) => (
            <Controller
              control={control}
              name="folderId"
              render={({ field }) => (
                <SelectMenu
                  key={field.value || 'empty'}
                  id={id}
                  invalid={invalid}
                  aria-describedby={describedBy}
                  value={field.value}
                  onValueChange={field.onChange}
                  placeholder={t('cases.folderPlaceholder')}
                >
                  {flattenFolders(folders ?? []).map((option) => (
                    <SelectMenuItem key={option.id} value={option.id}>
                      {option.label}
                    </SelectMenuItem>
                  ))}
                </SelectMenu>
              )}
            />
          )}
        </Field>
        <Field label={t('common.status')} error={errors.status?.message}>
          {({ id, describedBy }) => (
            <Controller
              control={control}
              name="status"
              render={({ field }) => (
                <SelectMenu
                  id={id}
                  aria-describedby={describedBy}
                  value={field.value}
                  onValueChange={field.onChange}
                >
                  {CASE_STATUSES.map((value) => (
                    <SelectMenuItem key={value} value={value}>
                      {caseStatus(value)}
                    </SelectMenuItem>
                  ))}
                </SelectMenu>
              )}
            />
          )}
        </Field>
        <Field label={t('labels.title')}>
          {({ id }) => (
            <LabelPicker
              id={id}
              projectCode={projectCode}
              value={labelIds}
              onChange={setLabelIds}
            />
          )}
        </Field>
        <Field label={t('common.environment')} error={errors.environmentId?.message}>
          {({ id, invalid, describedBy }) => (
            <Controller
              control={control}
              name="environmentId"
              render={({ field }) => (
                <EnvironmentSelect
                  id={id}
                  invalid={invalid}
                  describedBy={describedBy}
                  projectCode={projectCode}
                  value={field.value ?? null}
                  onChange={field.onChange}
                  enabled={open}
                />
              )}
            />
          )}
        </Field>
        <Field label={t('common.layer')} error={errors.layerId?.message}>
          {({ id, invalid, describedBy }) => (
            <Controller
              control={control}
              name="layerId"
              render={({ field }) => (
                <CaseAttributeSelect
                  id={id}
                  invalid={invalid}
                  describedBy={describedBy}
                  projectCode={projectCode}
                  kind="layer"
                  value={field.value ?? null}
                  onChange={field.onChange}
                  enabled={open}
                />
              )}
            />
          )}
        </Field>
        <Field label={t('common.module')} error={errors.moduleId?.message}>
          {({ id, invalid, describedBy }) => (
            <Controller
              control={control}
              name="moduleId"
              render={({ field }) => (
                <CaseAttributeSelect
                  id={id}
                  invalid={invalid}
                  describedBy={describedBy}
                  projectCode={projectCode}
                  kind="module"
                  value={field.value ?? null}
                  onChange={field.onChange}
                  enabled={open}
                />
              )}
            />
          )}
        </Field>
        <Field label={t('common.description')} error={errors.description?.message}>
          {({ id, invalid }) => <Textarea id={id} invalid={invalid} {...register('description')} />}
        </Field>
        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
      </form>
    </Dialog>
  );
};
